// The arithmetic of the triage page (milestone 4 check 9; B181), kept out of the components so
// it can be proved without a DOM: how two stores' passes become one list (D12, D43), how a
// list becomes the stacks the artboard draws (U2, U5), which standing actions each stack
// carries (module 10 build choice of 2026-09-14; U5 for the quick wins; D77 for a private
// item), and what "Park until…" offers (ItemStates: later today 16:00, tomorrow 08:00,
// Friday, next week, pick a time; a time and never a reason, D65).
import type { DecisionLabel, PassItem } from './api.js';
import { claimLinesFirst } from './claim.js';
import { isBack } from './rotation.js';

/**
 * Two stores, one list (D12, D43, U3). The stores are never joined in the database; each
 * pass is its own request and the app merges them here. Each store's list keeps the order
 * its pass gave (its rank), which is the order the master chose when it chose (D221, B439),
 * never the rules' score: re-sorting by score threw the master's reading away (BUGS.md T53,
 * 6 Oct 2026: its 2 scored 40.7 and its 9 scored 91.5). Two stores are merged by place
 * within a tier, the first store first at each place, with an imminence override in front
 * of everything in its tier (D12: a hard override, not a weighting). The rank shown is then
 * re-numbered over the merged list, since two passes both have a rank 1.
 */
export function mergeStores(lists: PassItem[][]): PassItem[] {
  const placed = lists.flatMap((list, store) => {
    const order = new Map(list.map((it, i) => [it.id, i]));
    const own = [...list].sort((a, b) =>
      a.tier - b.tier
      || Number(b.imminentOverride) - Number(a.imminentOverride)
      || a.rankNo - b.rankNo
      || (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    let tier = 0, n = 0;
    return own.map((it) => {
      if (it.tier !== tier) { tier = it.tier; n = 0; }
      n += 1;
      return { it, store, place: n };
    });
  });
  const merged = placed.sort((a, b) =>
    a.it.tier - b.it.tier
    || Number(b.it.imminentOverride) - Number(a.it.imminentOverride)
    || a.place - b.place
    || a.store - b.store);
  let tier = 0, n = 0;
  return merged.map(({ it }) => {
    if (it.tier !== tier) { tier = it.tier; n = 0; }
    n += 1;
    return { ...it, rankNo: n };
  });
}

export interface Stacks {
  /** Tier 1, the first ten in rank order (U5). */
  ten: PassItem[];
  /** What sits at eleven and beyond; it moves up as the ten clear (U2, U5) and is not drawn. */
  overflow: PassItem[];
  /** Tier 4, the strip of one-liners (U4). */
  awareness: PassItem[];
}

export const TEN = 10;

/** The stacks the page draws, from one merged list. Tier 3 is the overflow behind the ten (contract). */
export function stacks(items: PassItem[], top: number = TEN, chosen = false): Stacks {
  // U2, U5: what sat at eleven sits at ten as one clears. The ten is the first ten of tier 1 and then tier 3
  // (the overflow behind them) in rank order, so clearing one item in the page pulls the next up at once
  // rather than leaving nine until the next pass (found live by Mike, 2026-09-16).
  // D254: the top holds as many as the pass says (twenty from Cobrain 0.3, ten before it). And a list the master
  // CHOSE is never topped up: since D221 tier 3 behind a chosen list is what the master left out as settled, stale
  // or not the person's, so pulling it up puts a finished card back under his eyes. Up to twenty, never padded.
  // Module 04 B2220 (Cobrain 0.13, BUGS.md T55; Mike by pop-up, 7 Oct about 06:00 CEST, "Every card due, on a longer
  // list"): every card back by the rotation sits in tier 1 after the chosen ones, beyond the top, and is drawn on the list
  // whatever the top holds, so the list can be longer than twenty. What is beyond the top and not back waits as before.
  const first = items.filter((i) => i.tier === 1);
  const ranked = chosen ? first : [...first, ...items.filter((i) => i.tier === 3)];
  const beyond = ranked.slice(top);
  // REQ-2026-10-13-16 (Mike, 13 Oct 2026: "Can we remove quick wins in triage, I do not see the point"): no Quick wins
  // block. Cobrain 0.18 places nothing in tier 2; a pass from an earlier version that still holds a tier-2 item has it drawn
  // at the end of the list, so nothing it raised goes unseen.
  return {
    ten: [...ranked.slice(0, top), ...beyond.filter(isBack), ...items.filter((i) => i.tier === 2)],
    overflow: chosen ? [...first.slice(top).filter((i) => !isBack(i)), ...items.filter((i) => i.tier === 3)] : beyond.filter((i) => !isBack(i)),
    // Milestone 10 (U25 state 3): who has what leads the strip; a take reduces nobody's awareness.
    awareness: claimLinesFirst(items.filter((i) => i.tier === 4)),
  };
}

export type Standing = 'park' | 'complete' | 'dismiss' | 'delegate' | 'why' | 'not_private';

export const STANDING_WORDS: Record<Standing, string> = {
  park: 'Park until…', complete: 'Complete', dismiss: 'Dismiss', delegate: 'Delegate', why: 'Why is this here?', not_private: 'Not private?',
};

/**
 * The standing row. Every item in the ten offers the five (module 10 build choice, kept by
 * the milestone); a quick win offers Park until, Dismiss and Why (U5); a private item adds
 * "Not private?" (D77). Complete and dismiss are always both offered in the ten (D70).
 */
export function standingActions(item: Pick<PassItem, 'tier' | 'private'>): Standing[] {
  const row: Standing[] = item.tier === 2 ? ['park', 'dismiss', 'why'] : ['park', 'complete', 'dismiss', 'delegate', 'why'];
  return item.private ? [...row, 'not_private'] : row;
}

export interface ParkPreset { key: string; label: string; until: Date | null }

const at = (d: Date, h: number, m = 0): Date => { const x = new Date(d); x.setHours(h, m, 0, 0); return x; };
const plusDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/**
 * The park chooser (ItemStates, D65). Every preset is a time in the reader's own zone; the
 * database refuses a time that is not after now (work.park: parked_until > parked_at), so
 * "Later today, 16:00" is offered only while 16:00 is still ahead, and "Friday" means the
 * coming Friday at 08:00, never today. "Next week" is Monday 08:00. "Pick a time…" carries
 * no time of its own: the input supplies it.
 */
export function parkPresets(now: Date = new Date()): ParkPreset[] {
  const out: ParkPreset[] = [];
  const later = at(now, 16);
  if (later.getTime() > now.getTime()) out.push({ key: 'later_today', label: 'Later today, 16:00', until: later });
  out.push({ key: 'tomorrow', label: 'Tomorrow 08:00', until: at(plusDays(now, 1), 8) });
  const day = now.getDay();                       // 0 Sunday … 5 Friday
  let toFriday = (5 - day + 7) % 7;
  if (toFriday === 0) toFriday = 7;
  out.push({ key: 'friday', label: 'Friday', until: at(plusDays(now, toFriday), 8) });
  let toMonday = (1 - day + 7) % 7;
  if (toMonday === 0) toMonday = 7;
  out.push({ key: 'next_week', label: 'Next week', until: at(plusDays(now, toMonday), 8) });
  out.push({ key: 'pick', label: 'Pick a time…', until: null });
  return out;
}

/** The why panel's four adjustments (ItemStates, D30, D83): the words and the label each posts. */
export interface Adjustment { key: string; label: DecisionLabel | null; words: string; note: string }
export const ADJUSTMENTS: Adjustment[] = [
  { key: 'lower', label: 'lower', words: 'Should be lower', note: 'lower' },
  { key: 'higher', label: 'higher', words: 'Should be higher', note: 'higher' },
  { key: 'never', label: 'never', words: 'Should not be raised', note: 'never' },
  // The label vocabulary (D83) has no word for "not mine"; the note carries it until it does.
  { key: 'not_mine', label: null, words: 'Not my item', note: 'not_mine' },
];

/** "Judged by Cobrain's reasoning model; Cobrain's own model agreed", from features.brain (D121; U32: never the model's name). */
export function judgedBy(brain: { decider: string; version: string; shadow_agreed: boolean | null } | null | undefined): string | null {
  if (!brain) return null;
  // U32: no model's name on a screen; the master is "Cobrain's reasoning model", whichever provider it is (D120).
  const who = brain.decider === 'cobrain' ? 'Cobrain’s own model' : brain.decider === 'rule' ? 'a rule' : brain.decider === 'human' ? 'you' : 'Cobrain’s reasoning model';
  const agreed = brain.shadow_agreed === null || brain.shadow_agreed === undefined ? '' : brain.shadow_agreed ? '; Cobrain’s own model agreed' : '; Cobrain’s own model disagreed';
  return `Judged by ${who}${agreed}`;
}

/** The interrupt banner (ItemStates, D12, D46): a clocked item inside the hour, not yet handled. */
export function imminent(list: Array<{ id: string; kind: string; at: string | null; readAt: string | null }>, handled: ReadonlySet<string>, now: number = Date.now()): typeof list {
  return list.filter((n) => {
    if (n.readAt || handled.has(n.id)) return false;
    // The interrupt is for a clocked item inside the hour only (D12, D46; module 10 DESIGN). A
    // triage_urgent line says an item is in the ten, which the page under the banner already shows;
    // drawn here it read "Now:" with "On my way" over a promise sixteen days old (Mike, 2026-09-18).
    if (n.kind !== 'clocked_item' || !n.at) return false;
    const t = Date.parse(n.at);
    return !Number.isNaN(t) && t - now <= 60 * 60_000 && t - now > -15 * 60_000;
  });
}

/**
 * The banner's words without the time the pass wrote into them ("In 30 minutes: ..."): the banner
 * puts its own, counted from the clocked instant as it is now, in front, and the pass's figure goes
 * stale the minute it is written ("In 29 minutes: In 30 minutes: Collect Michael from school").
 */
export function bannerBody(body: string): string {
  return body.replace(/^In \d+ minutes?: /, '');
}

/** "In 45 minutes", "now", from the clocked instant. */
export function inWords(at: string | null, now: number = Date.now()): string {
  if (!at) return '';
  const min = Math.round((Date.parse(at) - now) / 60_000);
  if (Number.isNaN(min)) return '';
  if (min <= 0) return 'Now';
  if (min < 60) return `In ${min} minute${min === 1 ? '' : 's'}`;
  const h = Math.floor(min / 60), m = min % 60;
  return `In ${h} hour${h === 1 ? '' : 's'}${m ? ` ${m} min` : ''}`;
}
